import request from "../clientsApi";
import apiClient from "../apiClient";
import { API_ROUTES } from "../../config/baseURL";

// ─── Registro PAI ─────────────────────────────────────────────────────────────

export const storeRegistro = async (payload) => {
  const res = await request(API_ROUTES.paiRegistro, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const updateEsquemaVacunacion = async (payload) => {
  const res = await request(API_ROUTES.paiUpdateEsquemaVacunacion, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const updatePaiHistorial = async (payload) => {
  const res = await request(API_ROUTES.paiUpdateHistorial, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const getPacienteCompleto = async (identificacion) => {
  const res = await request(API_ROUTES.paiPaciente(identificacion), { method: "GET" });
  return res.json();
};

export const exportReportePAI = async (payload) => {
  try {
    const response = await apiClient({
      url: API_ROUTES.paiReporte,
      method: "POST",
      data: payload,
      responseType: "blob",
      timeout: 300000, // 5 min timeout
    });
    
    // Extraer filename de los headers si existe
    const contentDisposition = response.headers['content-disposition'];
    let filename = payload.formato === 'csv' ? 'Reporte_PAI.csv' : 'Reporte_PAI.xlsx';
    if (contentDisposition && contentDisposition.includes("filename=")) {
      filename = contentDisposition.split("filename=")[1].replace(/"/g, "");
    }

    return {
      blob: new Blob([response.data], { 
        type: payload.formato === 'csv' 
          ? 'text/csv' 
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      }),
      filename
    };
  } catch (error) {
    // Con responseType: 'blob', el error viene como Blob — hay que leerlo como texto
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json.detail || json.message || text);
      } catch (parseErr) {
        if (parseErr.message !== text) throw parseErr;
        throw new Error(text);
      }
    }
    throw error;
  }
};

// ─── Catálogo de configuración dinámica ───────────────────────────────────────

/**
 * Catálogo de vacunas activas desde BD.
 * Normaliza el shape para que coincida con el renderizador (VaccineCard):
 * { id, name, consecutivo_id, grupo, bg_color, text_color, fields }
 */
export const getConfigVacunas = async () => {
  const res = await request(API_ROUTES.paiConfigVacunas, { method: "GET" });
  const payload = await res.json();

  return (payload.data || []).map((v) => ({
    id: v.codigo,
    name: v.descripcion,
    consecutivo_id: v.consecutivo_id,
    grupo: v.grupo,
    bg_color: v.bg_color,
    text_color: v.text_color,
    fields: v.fields || [],
    idTipoObligatoriedad: v.idTipoObligatoriedad,
  }));
};

/**
 * Secciones del formulario de datos del paciente desde BD.
 * Normaliza el shape: { id, name, color, fields }
 */
export const getConfigSecciones = async () => {
  const res = await request(API_ROUTES.paiConfigSecciones, { method: "GET" });
  const payload = await res.json();

  return (payload.data || []).map((s) => ({
    id: s.codigo,
    name: s.descripcion,
    color: s.color,
    text_color: s.text_color,
    fields: s.fields || [],
    idTipoObligatoriedad: s.idTipoObligatoriedad,
  }));
};

/**
 * Catálogos auxiliares PAI (países, departamentos, municipios, aseguradoras).
 */
export const getConfigCatalogos = async () => {
  const res = await request(API_ROUTES.paiConfigCatalogos, { method: "GET" });
  const payload = await res.json();
  return payload.data || {
    paises: [],
    departamentos: [],
    municipios: [],
    aseguradoras: [],
  };
};

/**
 * Usuarios vacunadores activos (es_vacunador = 1).
 * Usado para poblar el campo 'responsable' en el formulario PAI.
 */
export const getVacunadores = async () => {
  const res = await request(API_ROUTES.getVacunadores, { method: "GET" });
  const payload = await res.json();
  return (payload.data || []).map((u) => ({
    id: u.Id_usuario,
    nombre: u.nombre,
  }));
};

/**
 * Elementos de catálogo dinámicos desde dbo.elementosisa.
 * Garantiza opciones con value estrictamente numérico (INT).
 */
export const getElementos = async (form = "pai", label = "") => {
  const url = API_ROUTES.elementos ? API_ROUTES.elementos(form, label) : `/apiAPIM/elementos?form=${form}${label ? `&label=${label}` : ''}`;
  const res = await request(url, { method: "GET" });
  const payload = await res.json();
  return payload.data || {};
};
